import * as React from 'react';
import { Stepper } from 'frontend-toolkit';
import { useMutation, useQuery } from '@apollo/client';
import * as CreateOrderBatch from 'core/gql/mutations/create-order-batch';
import Breadcrumb, { BreadcrumbSection } from 'components/common/breadcrumb';
import Installments from 'components/registration/v2/Installments';
import MerchantAccountSelect from 'components/registration/v2/MerchantAccountSelect';
import * as ListPaymentAccounts from 'core/gql/queries/list-payment-accounts';
import SendInvoice from './SendInvoice';
import * as ListInstallmentPlans from 'core/gql/queries/list-installment-plans';
import { reactiveFeedbackMessage } from 'core/apollo-cache';

interface Props {
  orgId: string;
  setIsOpen: (boolean) => void;
}

export enum InvoieGuideSteps {
  DEPOSIT_ACCOUNT = 'Deposit Account',
  INVOICE = 'Create invoice',
  INSTALLMENT_PLAN = 'Payment Plan',
}

const NewInvoice = ({ orgId, setIsOpen }: Props) => {
  const paymentAccountInfoDefault = {
    paymentAccountId: null,
    organizationPaysProcessingFee: false,
    allowOfflineCheckout: false,
  };

  const [currentStep, setCurrentStep] = React.useState(0);
  const [orderBatchId, setOrderBatchId] = React.useState(null);

  const [dueDate, setDueDate] = React.useState(null);
  const [userIds, setUserIds] = React.useState([]);
  const [orderLineItems, setOrderLineItems] = React.useState([]);
  const [paymentAccountInfo, setPaymentAccountInfo] = React.useState(paymentAccountInfoDefault);

  const getInstallmentPlans = useQuery(ListInstallmentPlans.QUERY, {
    variables: { scopeId: orderBatchId, scopeType: 'order_batch' },
    skip: !orderBatchId,
  });

  const installmentPlans = getInstallmentPlans.data?.listInstallmentPlans || [];

  const paymentAccounts =
    useQuery(ListPaymentAccounts.QUERY, {
      variables: { scopeId: +orgId, scopeType: 'organization' },
    })?.data?.listPaymentAccounts || [];

  const [createOrderBatchRequest] = useMutation(CreateOrderBatch.MUTATION);

  const breadcrumbDefaults: BreadcrumbSection[] = [
    {
      name: 'New Invoice Batch',
      onClick: () => {
        updateCurrentBreadcrumb(InvoieGuideSteps.INSTALLMENT_PLAN);
        setCurrentStep(0);
      },
      children: [
        {
          name: InvoieGuideSteps.DEPOSIT_ACCOUNT,
          onClick: () => {
            updateCurrentBreadcrumb(InvoieGuideSteps.DEPOSIT_ACCOUNT);
            setCurrentStep(0);
          },
        },
        {
          name: InvoieGuideSteps.INVOICE,
          onClick: () => {
            updateCurrentBreadcrumb(InvoieGuideSteps.INVOICE);
            setCurrentStep(1);
          },
        },
        {
          name: InvoieGuideSteps.INSTALLMENT_PLAN,
          onClick: () => {
            updateCurrentBreadcrumb(InvoieGuideSteps.INSTALLMENT_PLAN);
            setCurrentStep(2);
          },
        },
      ],
    },
  ];

  const [breadcrumb, updateBreadcrumb] = React.useState(breadcrumbDefaults);
  const [currentBreadcrumb, updateCurrentBreadcrumb] = React.useState(InvoieGuideSteps.DEPOSIT_ACCOUNT);

  const closeStepper = () => {
    setIsOpen(false);
    resetInvoiceGuide();
  };

  const resetInvoiceGuide = () => {
    setCurrentStep(0);
    updateBreadcrumb(breadcrumbDefaults);
    updateCurrentBreadcrumb(InvoieGuideSteps.DEPOSIT_ACCOUNT);
  };

  const createOrderBatch = async () => {
    try {
      // do not create another order batch if it already exists
      if (
        !orderBatchId &&
        dueDate &&
        orderLineItems.length > 0 &&
        userIds.length > 0 &&
        paymentAccountInfo.paymentAccountId
      ) {
        const response = await createOrderBatchRequest({
          variables: {
            scopeId: orgId,
            scopeType: 'organization',
            allowOfflineCheckout: paymentAccountInfo.allowOfflineCheckout,
            paymentAccountPaysProcessingFee: paymentAccountInfo.organizationPaysProcessingFee,
            paymentAccountId: paymentAccountInfo.paymentAccountId,
            userIds: userIds.map((id) => parseInt(id)),
            dueDate: dueDate,
            orderLineItems: orderLineItems.map((item) => ({
              ...item,
              amount: Math.round(parseFloat(item.amount) * 100),
            })),
          },
        });

        if (response?.data?.createOrderBatch?.id) {
          setOrderBatchId(response?.data?.createOrderBatch?.id);
        } else {
          reactiveFeedbackMessage({
            message:
              'Oh no! It looks like something went wrong. Please try again or contact TeamSnap Support for help.',
            isError: true,
            autoclose: true,
          });
        }
      }
    } catch (e) {
      reactiveFeedbackMessage({
        message: 'Oh no! It looks like something went wrong. Please try again or contact TeamSnap Support for help.',
        isError: true,
        autoclose: true,
      });
      console.warn('FAILED!');
    }
  };

  return (
    <>
      <Stepper
        show
        showProgress={false}
        activeStep={currentStep}
        onClose={closeStepper}
        drawer={<Breadcrumb current={currentBreadcrumb} breadcrumb={breadcrumb} data={null} />}
        customStepStyles={{ paddingLeft: '300px' }}
        steps={[
          {
            title: 'Merchant Account',
            disableNext: !paymentAccountInfo.paymentAccountId,
            heading: 'Select deposit account',
            subheading: 'Fees collected for this invoice batch will be deposited to the selected account.',
            elements: (
              <MerchantAccountSelect
                orgId={+orgId}
                info={paymentAccountInfo}
                onChange={setPaymentAccountInfo}
                accounts={paymentAccounts}
              />
            ),
            hideIfBlank: true,
            stepCallback: () => {
              updateCurrentBreadcrumb(InvoieGuideSteps.INVOICE);
              setCurrentStep(1);
            },
          },
          {
            title: 'Invoices',
            disableNext: dueDate === null || orderLineItems.length === 0 || userIds.length === 0,
            heading: 'Create invoices',
            subheading: 'Set due date and user IDS for sending invoices',
            stepBackCallback: () => {
              updateCurrentBreadcrumb(InvoieGuideSteps.DEPOSIT_ACCOUNT);
              setCurrentStep(0);
            },
            stepCallback: () => {
              updateCurrentBreadcrumb(InvoieGuideSteps.INSTALLMENT_PLAN);
              setCurrentStep(2);
            },
            submitCallback: () => {
              createOrderBatch();
            },
            elements: (
              <SendInvoice
                orgId={orgId}
                dueDate={dueDate}
                setDueDate={setDueDate}
                userIds={userIds}
                setUserIds={setUserIds}
                orderLineItems={orderLineItems}
                setOrderLineItems={setOrderLineItems}
              />
            ),
          },
          {
            title: 'Installments',
            heading: 'Create Payment Plan(s)',
            disableNext: installmentPlans.length === 0,
            elements: <Installments scopeId={+orderBatchId} scopeType="order_batch" orgId={+orgId} />,
            stepCallback: () => {
              closeStepper();
            },
            stepBackCallback: () => {
              updateCurrentBreadcrumb(InvoieGuideSteps.INVOICE);
              setCurrentStep(1);
            },
            customNextButtonText: 'Send Invoices',
          },
        ]}
      />
    </>
  );
};

export default NewInvoice;
